import React from "react";
import { Navigate } from "react-router-dom";
import Base from "./views/Base";
import MyHotelsLayout from "./views/v2/MyHotelsLayout";
import V1Layout from "./views/V1Layout";
import CentralizeRedirect from "auth/CentralizeRedirect";
import ScrollToTop from "utils/ScrollToTop";
import { useAuth } from "react-oidc-context";
import PageAccessGuard from "./components/PageAccessGuard";
import HotelDefaultRedirect from "./components/HotelDefaultRedirect";

const wrapWithScrollToTop = (component) => <ScrollToTop>{component}</ScrollToTop>;

// Composant qui vérifie si l'utilisateur est authentifié avant d'afficher le contenu
const PrivateRoute = ({ children }) => {
  const auth = useAuth();

  // Si l'utilisateur n'est pas authentifié, rediriger vers la page d'accueil
  if (!auth.isAuthenticated && !auth.isLoading) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Fonction pour wrapper les composants avec PrivateRoute et ScrollToTop
const wrapWithPrivateRoute = (component) => <PrivateRoute>{wrapWithScrollToTop(component)}</PrivateRoute>;

// Fonction pour wrapper les composants avec PageAccessGuard, PrivateRoute et ScrollToTop
const wrapWithPageAccess = (pageName, component, fallbackPath = "/hotel/lunishower/dashboard") => (
  <PrivateRoute>
    <ScrollToTop>
      <PageAccessGuard pageName={pageName} fallbackPath={fallbackPath} requireHotel={true} bypass={false}>
        {component}
      </PageAccessGuard>
    </ScrollToTop>
  </PrivateRoute>
);

// Composant wrapper pour V1Layout qui vérifie l'accès avant de rendre
const V1LayoutWithAccess = ({ pageName, fallbackPath = "/user/hotels" }) => {
  return (
    <PageAccessGuard pageName={pageName} requireHotel={true} bypass={false} fallbackPath={fallbackPath}>
      <V1Layout />
    </PageAccessGuard>
  );
};

const routes = [
  {
    path: "/hotel/",
    element: wrapWithPrivateRoute(<HotelDefaultRedirect />),
  },
  {
    path: "/settings/hotel/informations",
    element: wrapWithPageAccess("hotelInformations", <Base pageName="Mes réglages" viewID={"hotelInformations"} />),
  },
  {
    path: "/settings/hotel/rooms",
    element: wrapWithPageAccess(
      "rooms_settings",
      // <Base pageName="Gestion des chambres" viewID={"rooms_settings"} />
      <Base pageName="Les chambres" viewID={"rooms"} />
    ),
  },
  {
    path: "/hotel/lunishower",
    element: wrapWithPrivateRoute(<V1LayoutWithAccess pageName="dashboard" />),
    children: [
      {
        index: true,
        element: <Navigate to="/hotel/lunishower/dashboard" replace />,
      },
      {
        path: "dashboard",
        element: <V1LayoutWithAccess pageName="dashboard" />,
      },
      {
        path: "consommation",
        element: <V1LayoutWithAccess pageName="consommation" />,
      },
      {
        path: "economies",
        element: <V1LayoutWithAccess pageName="economies" />,
      },
      {
        path: "badges",
        element: <V1LayoutWithAccess pageName="badges" />,
      },
      {
        path: "stats",
        element: <V1LayoutWithAccess pageName="stats" />,
      },
      {
        path: "donation",
        element: <V1LayoutWithAccess pageName="donation" />,
      },
    ],
  },
  // Routes obsolètes gardées pour compatibilité mais redirigent vers le nouveau système
  {
    path: "/hotel/lunishower/rooms",
    element: <Navigate to="/hotel/lunishower/dashboard" replace />,
  },
  {
    path: "/hotel/lunishower/rooms/:roomId",
    element: <Navigate to="/hotel/lunishower/dashboard" replace />,
  },
  {
    path: "/hotel/lunishower/customer_xp",
    element: <Navigate to="/hotel/lunishower/dashboard" replace />,
  },
  {
    path: "/hotel/lunishower/abonnement",
    element: <Navigate to="/hotel/lunishower/dashboard" replace />,
  },
  {
    path: "/settings/hotel/access",
    element: wrapWithPageAccess("myAccess", <Base pageName="Gestion des accès" viewID={"myAccess"} />),
  },
  {
    path: "/settings/hotel/devices",
    element: wrapWithPageAccess("devices", <Base pageName="Gestion des appareils" viewID={"devices"} />),
  },
  {
    path: "/user/group/ajouter",
    element: wrapWithPrivateRoute(<Base pageName="Ajouter un groupe" viewID={"addGroup"} />),
  },
  {
    path: "/user/hotels/ajouter",
    element: wrapWithPrivateRoute(<Base pageName="Ajouter un hôtel" viewID={"addHotel"} />),
  },
  {
    path: "/user/informations",
    element: wrapWithPrivateRoute(<Base pageName="Mes informations" viewID={"personalInformations"} />),
  },
  {
    path: "/user/hotels",
    element: wrapWithPrivateRoute(<MyHotelsLayout />),
  },
  {
    path: "/user/statistics",
    element: wrapWithPrivateRoute(<Base pageName="Mes statistiques" viewID={"statistics"} />),
  },
  {
    path: "/user/preferences",
    element: wrapWithPrivateRoute(<Base pageName="Mes préférences" viewID={"preferences"} />),
  },
  {
    path: "/user/accessAPI",
    element: wrapWithPrivateRoute(<Base pageName="Accès API" viewID={"accessAPI"} />),
  },
  {
    path: "/inscription",
    element: wrapWithPrivateRoute(<Base pageName="Inscription" viewID={"inscription"} />),
  },
  {
    path: "/redirect",
    element: <CentralizeRedirect />,
  },
  {
    path: "/",
    // element: wrapWithScrollToTop(<Base pageName="Mon hôtel" viewID={"main"} />),
    element: wrapWithScrollToTop(<Base pageName="Accueil" viewID={"homepage"} />),
  },
  {
    path: "/hotel/lunishower/simulator",
    element: wrapWithPageAccess("simulator", <Base pageName="Simulator" viewID={"mySimulator"} />),
  },
  {
    path: "/hotel/lunishower/badges",
    element: wrapWithPageAccess("badges", <Base pageName="Badges" viewID={"badges"} />),
  },
  {
    path: "/hotel/lunishower/stats",
    element: wrapWithPageAccess("stats", <Base pageName="Stats" viewID={"stats"} />),
  },
  {
    path: "/simulateur-accor-groupe",
    element: wrapWithScrollToTop(<Base pageName="Simulateur LuniShower Accor" viewID={"mySimulator"} />),
  },
  {
    path: "/hotel/greenmiles",
    element: <Navigate to="/hotel/greenmiles/declined-services" replace />,
  },
  {
    path: "/hotel/greenmiles/declined-services",
    element: wrapWithPageAccess("declined-services", <Base pageName="Services déclinés" viewID={"declinedServices"} />, "/user/hotels"),
  },
  {
    path: "/hotel/greenmiles/dashboard",
    element: <Navigate to="/hotel/greenmiles/declined-services" replace />,
  },
  {
    path: "/hotel/greenmiles/rewards",
    element: <Navigate to="/hotel/greenmiles/declined-services" replace />,
  },
  {
    path: "/hotel/greenmiles/actionsrse",
    element: <Navigate to="/hotel/greenmiles/declined-services" replace />,
  },
  {
    path: "/hotel/greenmiles/luniclean-status",
    element: <Navigate to="/hotel/greenmiles/declined-services" replace />,
  },
  { path: "*", element: <Navigate to="/" replace /> },

  // Add more routes as needed
  // ...
];

export default routes;
